import { useMemo, useState } from "react";
import type { ModelsByProvider } from "../types";
import "./ModelSelect.css";

/**
 * Dependent provider/model picker (Task 4.8). Changing provider snaps the
 * model to that provider's first model, so an invalid {provider, model}
 * pair is unrepresentable. Renders a filterable text input above 100
 * models so the picker doesn't hang the browser on a large catalog
 * (featherless alone is 21,633 — excluded by default, but openrouter/
 * together are still large).
 */
export default function ModelSelect({
  catalog,
  provider,
  model,
  onChange,
  allowedProviders,
}: {
  catalog: ModelsByProvider;
  provider: string | null;
  model: string;
  onChange: (provider: string, model: string) => void;
  /** Restrict the provider dropdown — e.g. chat/research to tool_loop, vision to openai/google. */
  allowedProviders?: (p: string, supports: Record<string, boolean>) => boolean;
}) {
  const [filter, setFilter] = useState("");

  const providers = useMemo(() => {
    return Object.keys(catalog)
      .filter((p) => !allowedProviders || allowedProviders(p, catalog[p]?.supports || {}))
      .sort();
  }, [catalog, allowedProviders]);

  const models = useMemo(() => {
    if (!provider || !catalog[provider]) return [];
    return [...catalog[provider].models].sort((a, b) => a.id.localeCompare(b.id));
  }, [catalog, provider]);

  const filteredModels = useMemo(() => {
    if (!filter) return models;
    const f = filter.toLowerCase();
    return models.filter((m) => m.id.toLowerCase().includes(f));
  }, [models, filter]);

  const handleProviderChange = (p: string) => {
    const first = catalog[p]?.models[0]?.id || "";
    onChange(p, first);
  };

  if (providers.length === 0) {
    return <div className="dim model-select-empty">(refresh models)</div>;
  }

  return (
    <div className="model-select row">
      <select
        className="select"
        value={provider || ""}
        onChange={(e) => handleProviderChange(e.target.value)}
      >
        {!provider && <option value="">(select provider)</option>}
        {providers.map((p) => (
          <option key={p} value={p}>{p}</option>
        ))}
      </select>
      {!provider ? (
        <span className="dim model-select-empty">(select provider)</span>
      ) : models.length > 100 ? (
        <div className="model-select-combobox">
          <input
            className="select"
            list={`models-${provider}`}
            value={model}
            placeholder="Filter models…"
            onChange={(e) => {
              setFilter(e.target.value);
              onChange(provider, e.target.value);
            }}
          />
          <datalist id={`models-${provider}`}>
            {filteredModels.slice(0, 500).map((m) => (
              <option key={m.id} value={m.id} />
            ))}
          </datalist>
        </div>
      ) : (
        <select
          className="select model-select-model"
          value={model}
          onChange={(e) => onChange(provider, e.target.value)}
        >
          {/* A saved model that is no longer in the catalog (retired alias,
              stale override) would otherwise select nothing — the <select>
              renders blank and collapses to the arrow. Surface it instead. */}
          {model && !models.some((m) => m.id === model) && (
            <option value={model}>{model} (not in catalog)</option>
          )}
          {models.map((m) => (
            <option key={m.id} value={m.id}>{m.id}</option>
          ))}
        </select>
      )}
    </div>
  );
}
